/**
 * Returns shoppers to where they were on a collection page.
 *
 * 1. Listens for any link click on a /collections/ page.
 * 2. Saves that exact URL (incl. ?page= and filters), the scroll position,
 *    and which product card was clicked plus where it sat on screen.
 * 3. When the shopper comes back to the same URL, waits for results-list.js
 *    to finish sorting (it sets data-sb-ready), puts the clicked card back
 *    where it was, and clears the save.
 */
(() => {
  const KEY = 'sb-collection-scroll';

  if (!window.location.pathname.startsWith('/collections/')) return;

  // We restore the position ourselves once the grid is sorted; the browser's
  // own restore happens before the tagged products are added and lands wrong.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  const currentUrl = () => window.location.pathname + window.location.search;

  const clear = () => {
    try {
      sessionStorage.removeItem(KEY);
    } catch {}
  };

  // 1 + 2: save on any link click.
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href]');
      if (!link || link.getAttribute('href')?.startsWith('#')) return;

      const card = event.target.closest('li.product-grid__item');

      try {
        sessionStorage.setItem(
          KEY,
          JSON.stringify({
            url: currentUrl(),
            y: window.scrollY,
            productId: card instanceof HTMLElement ? card.dataset.productId || null : null,
            offset: card ? card.getBoundingClientRect().top : null,
          })
        );
      } catch {
        // Storage blocked: nothing to restore later.
      }
    },
    true
  );

  // Back button served the page from the browser's memory: nothing was
  // re-rendered and the position is already right, so just clear the save.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) clear();
  });

  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch {}

  if (!saved || saved.url !== currentUrl()) return;

  // 3: jump back and clear the save.
  const restore = () => {
    clear();

    const card = saved.productId
      ? document.querySelector(
          `li.product-grid__item[data-product-id="${CSS.escape(String(saved.productId))}"]`
        )
      : null;

    const top =
      card && typeof saved.offset === 'number'
        ? card.getBoundingClientRect().top + window.scrollY - saved.offset
        : Number(saved.y) || 0;

    window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  };

  // Wait until results-list.js has added and sorted the tagged products.
  const waitForSort = () => {
    const list = document.querySelector('results-list');

    if (!list || list.hasAttribute('data-sb-ready')) {
      restore();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!list.hasAttribute('data-sb-ready')) return;
      observer.disconnect();
      restore();
    });

    observer.observe(list, { attributes: true, attributeFilter: ['data-sb-ready'] });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForSort, { once: true });
  } else {
    waitForSort();
  }
})();